import { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source, Popup, NavigationControl, type LayerProps, type MapRef } from 'react-map-gl';
import type { LngLatBoundsLike } from 'mapbox-gl';
import { useSelector } from 'react-redux';
import { RootState, useAppSelector } from '../../redux/AppStore';

type MapPoint = {
  latitude: number;
  longitude: number;
};

type RouteStop = MapPoint & {
  serviceType: string;
  executionOrder?: number;
  shipmentOrder?: number;
  postCode?: string;
  markerLabel?: string;
  trackingCode?: string;
};

export default function OrderDetailMapComponent() {
  const trackingInfo = useAppSelector((state: RootState) => state.tracking.trackingAddress);
  const trackingdata = trackingInfo?.tracking_address || [];
  const trackingNumber = useAppSelector((state: RootState) => state.tracking.trackingNum);
  const driverInfo = useAppSelector((state: RootState) => state.tracking.driverInfo);
  const [activePopup, setActivePopup] = useState<{
    longitude: number;
    latitude: number;
    postCode?: string;
    label?: string;
  } | null>(null);
  const [routeGeoJson, setRouteGeoJson] = useState<any | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef<MapRef | null>(null);
  const mapboxToken =
    "pk.eyJ1IjoiaW5zdGFkaXNwYXRjaCIsImEiOiJjazJvajJueWwwNjlmM2dwcHMxbTFiMHl0In0.9eyVqMCN3WIodGdslgQ1hA";
  const driverIconUrl = `${window.location.origin}/img/mini-van-green.svg`;

  const routeStops = useMemo<RouteStop[]>(() => {
    if (!Array.isArray(trackingdata)) {
      return [];
    }

    let sameDayLoadId = trackingInfo?.loadId || '';
    for (const d of trackingdata) {
      if (
        d.group_type === "SAME" &&
        (d.shipment_ticket === trackingNumber ||
          d.load_identity === trackingNumber ||
          (sameDayLoadId && d.load_identity === sameDayLoadId))
      ) {
        sameDayLoadId = d.load_identity;
        break;
      }
    }

    if (sameDayLoadId) {
      const sameDayStops = trackingdata
        .filter(d => d.group_type === "SAME" && d.load_identity === sameDayLoadId)
        .map((d, index) => ({
          latitude: Number(d.shipment_latitude ?? 0),
          longitude: Number(d.shipment_longitude ?? 0),
          serviceType: d.shipment_service_type,
          executionOrder: Number(d.execution_order ?? index),
          shipmentOrder: Number(d.shipment_order ?? Number.NaN),
          postCode: d.post_code,
          trackingCode: d.tracking_code,
          index,
        }))
        .filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
        .sort((a, b) => {
          const aOrder = Number.isFinite(a.shipmentOrder) ? a.shipmentOrder : a.executionOrder;
          const bOrder = Number.isFinite(b.shipmentOrder) ? b.shipmentOrder : b.executionOrder;
          if (aOrder === bOrder) return a.index - b.index;
          return aOrder - bOrder;
        })
        .map(({ index, ...rest }) => rest);

      let pickupCounter = 0;
      let dropCounter = 0;
      return sameDayStops.map(stop => {
        const hasShipmentOrder = Number.isFinite(stop.shipmentOrder);
        const displayOrder = hasShipmentOrder ? Number(stop.shipmentOrder) + 1 : null;
        if (stop.serviceType === "P") {
          pickupCounter += 1;
          const labelNumber = displayOrder ?? pickupCounter;
          return { ...stop, markerLabel: `P${labelNumber}` };
        }
        dropCounter += 1;
        const labelNumber = displayOrder ?? dropCounter;
        return { ...stop, markerLabel: `D${labelNumber}` };
      });
    }

    let pickupPoint: RouteStop | null = null;
    let dropPoint: RouteStop | null = null;

    for (const d of trackingdata) {
      const latitude = Number(d.shipment_latitude ?? 0);
      const longitude = Number(d.shipment_longitude ?? 0);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        continue;
      }

      const matchesTracking =
        d.load_identity === trackingNumber || d.shipment_ticket === trackingNumber;

      if (!matchesTracking) {
        continue;
      }

      if (d.shipment_service_type === "P" && !pickupPoint) {
        pickupPoint = {
          latitude,
          longitude,
          serviceType: d.shipment_service_type,
          postCode: d.post_code,
          markerLabel: "P1",
        };
      }

      if (d.shipment_service_type === "D" && !dropPoint) {
        dropPoint = {
          latitude,
          longitude,
          serviceType: d.shipment_service_type,
          postCode: d.post_code,
          markerLabel: "D1",
        };
      }
    }

    const stops: RouteStop[] = [];
    if (pickupPoint) stops.push(pickupPoint);
    if (dropPoint) stops.push(dropPoint);
    return stops;
  }, [trackingdata, trackingNumber]);

  const isRouteStarted = String(driverInfo?.is_route_started ?? '0') === '1';
  const driverPoint = useMemo<MapPoint | null>(() => {
    if (!isRouteStarted) {
      return null;
    }
    const latitude = Number(driverInfo?.lat ?? 0);
    const longitude = Number(driverInfo?.long ?? 0);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }
    if (latitude === 0 && longitude === 0) {
      return null;
    }
    return { latitude, longitude };
  }, [driverInfo, isRouteStarted]);

  const hasAnyPoint = routeStops.length > 0 || Boolean(driverPoint);
  const fallbackPoint = routeStops[0] || driverPoint || { latitude: 0, longitude: 0 };
  const openMapInNewTab = () => {
    const stops = routeStops.map(stop => ({
      latitude: stop.latitude,
      longitude: stop.longitude,
      serviceType: stop.serviceType,
      postCode: stop.postCode,
      markerLabel: stop.markerLabel,
      trackingCode: stop.trackingCode,
    }));
    const driver = driverPoint
      ? { latitude: driverPoint.latitude, longitude: driverPoint.longitude }
      : null;
    const initial = {
      latitude: fallbackPoint.latitude,
      longitude: fallbackPoint.longitude,
    };
    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Route Map</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <link href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css" rel="stylesheet" />
    <style>
      html, body { margin: 0; padding: 0; height: 100%; }
      #map { position: absolute; top: 0; bottom: 0; width: 100%; }
      .marker {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        cursor: pointer;
      }
      .marker.pickup { border: 2px solid #1e88e5; color: #1e88e5; }
      .marker.drop { border: 2px solid #d32f2f; color: #d32f2f; }
      .driver-marker img { width: 28px; height: 28px; }
    </style>
  </head>
  <body>
    <div id="map"></div>
    <script src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"></script>
    <script>
      mapboxgl.accessToken = ${JSON.stringify(mapboxToken)};
      const stops = ${JSON.stringify(stops)};
      const driver = ${JSON.stringify(driver)};
      const initial = ${JSON.stringify(initial)};

      const map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/mapbox/streets-v12",
        center: [initial.longitude, initial.latitude],
        zoom: 12,
      });

      map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

      map.on("load", () => {
        const drawRoute = (coordinates) => {
          map.addSource("route", {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: { type: "LineString", coordinates },
              properties: {},
            },
          });
          map.addLayer({
            id: "route-line",
            type: "line",
            source: "route",
            paint: { "line-color": "#1e88e5", "line-width": 4, "line-opacity": 0.9 },
            layout: { "line-join": "round", "line-cap": "round" },
          });
        };

        if (stops.length >= 2) {
          if (stops.length > 25) {
            drawRoute(stops.map(s => [s.longitude, s.latitude]));
          } else {
            const coords = stops.map(s => s.longitude + "," + s.latitude).join(";");
            const url = "https://api.mapbox.com/directions/v5/mapbox/driving/" + coords +
              "?geometries=geojson&overview=full&access_token=" + ${JSON.stringify(mapboxToken)};
            fetch(url)
              .then(res => res.json())
              .then(data => {
                const geometry = data && data.routes && data.routes[0] && data.routes[0].geometry;
                if (geometry && geometry.coordinates) {
                  drawRoute(geometry.coordinates);
                } else {
                  drawRoute(stops.map(s => [s.longitude, s.latitude]));
                }
              })
              .catch(() => drawRoute(stops.map(s => [s.longitude, s.latitude])));
          }
        }

        const bounds = new mapboxgl.LngLatBounds();
        let hasBounds = false;
        stops.forEach(stop => {
          bounds.extend([stop.longitude, stop.latitude]);
          hasBounds = true;
        });
        if (driver) {
          bounds.extend([driver.longitude, driver.latitude]);
          hasBounds = true;
        }
        if (hasBounds && !bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 40 });
        }
      });

      stops.forEach(stop => {
        const el = document.createElement("div");
        el.className = "marker " + (stop.serviceType === "P" ? "pickup" : "drop");
        if (stop.trackingCode === "DELIVERYSUCCESS") {
          el.style.backgroundColor = "#0d47a1";
          el.style.color = "#ffffff";
        }
        el.textContent = stop.markerLabel || (stop.serviceType === "P" ? "P" : "D");
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
          "<div style='font-size:12px'>" +
            (stop.markerLabel ? "<div style='font-weight:700;margin-bottom:4px'>" + stop.markerLabel + "</div>" : "") +
            (stop.postCode || "Postcode not available") +
          "</div>"
        );
        new mapboxgl.Marker(el)
          .setLngLat([stop.longitude, stop.latitude])
          .setPopup(popup)
          .addTo(map);
      });

      if (driver) {
        const el = document.createElement("div");
        el.className = "driver-marker";
        const img = document.createElement("img");
        img.src = ${JSON.stringify(driverIconUrl)};
        img.alt = "Driver";
        el.appendChild(img);
        new mapboxgl.Marker(el)
          .setLngLat([driver.longitude, driver.latitude])
          .addTo(map);
      }
    </script>
  </body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    const newWindow = window.open(blobUrl, "_blank");
    if (newWindow) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } else {
      URL.revokeObjectURL(blobUrl);
    }
  };

  const bounds: LngLatBoundsLike | undefined = useMemo(() => {
    const points = [...routeStops, ...(driverPoint ? [driverPoint] : [])];
    if (points.length < 2) {
      return undefined;
    }
    let minLat = points[0].latitude;
    let maxLat = points[0].latitude;
    let minLng = points[0].longitude;
    let maxLng = points[0].longitude;
    for (const p of points) {
      minLat = Math.min(minLat, p.latitude);
      maxLat = Math.max(maxLat, p.latitude);
      minLng = Math.min(minLng, p.longitude);
      maxLng = Math.max(maxLng, p.longitude);
    }
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as LngLatBoundsLike;
  }, [routeStops, driverPoint]);

  useEffect(() => {
    let isMounted = true;
    const buildRoute = async () => {
      if (routeStops.length < 2) {
        setRouteGeoJson(null);
        return;
      }
      if (routeStops.length > 25) {
        setRouteGeoJson({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: routeStops.map(stop => [stop.longitude, stop.latitude]),
              },
              properties: {},
            },
          ],
        });
        return;
      }
      const coords = routeStops.map(stop => `${stop.longitude},${stop.latitude}`).join(';');
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}?geometries=geojson&overview=full&access_token=${mapboxToken}`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Directions request failed');
        const data = await response.json();
        const geometry = data?.routes?.[0]?.geometry;
        if (geometry && isMounted) {
          setRouteGeoJson({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry,
                properties: {},
              },
            ],
          });
          return;
        }
      } catch {
        // fallback below
      }
      if (isMounted) {
        setRouteGeoJson({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: routeStops.map(stop => [stop.longitude, stop.latitude]),
              },
              properties: {},
            },
          ],
        });
      }
    };
    buildRoute();
    return () => {
      isMounted = false;
    };
  }, [routeStops, mapboxToken]);

  useEffect(() => {
    if (!mapRef.current || !mapReady || !bounds) return;
    mapRef.current.fitBounds(bounds, { padding: 40 });
  }, [bounds, mapReady]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    // If the map was created while hidden, resize helps tiles render correctly.
    mapRef.current.resize();
  }, [mapReady, routeStops, driverPoint]);

  const lineLayer: LayerProps = {
    id: "pickup-drop-line",
    type: "line",
    paint: {
      "line-color": "#1e88e5",
      "line-width": 4,
      "line-opacity": 0.9,
    },
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
  };

  return (
    <div className="main_map">
      {mapError || !hasAnyPoint ? (
        <div
          style={{
            width: "100%",
            height: 350,
            background: "#e3f2fd",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 13, color: "#1e3a8a" }}>
            {mapError ? "Map failed to load. Please try again." : "Map data not available yet."}
          </div>
          <button
            type="button"
            onClick={openMapInNewTab}
            style={{
              background: "#ffffff",
              border: "1px solid #cfd8dc",
              borderRadius: 4,
              padding: "6px 10px",
              fontSize: 12,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          >
            Open map
          </button>
        </div>
      ) : (
        <Map
          mapboxAccessToken={mapboxToken}
          initialViewState={
            bounds
              ? {
                  bounds,
                  fitBoundsOptions: { padding: 40 },
                }
              : {
                  longitude: fallbackPoint.longitude,
                  latitude: fallbackPoint.latitude,
                  zoom: 10,
                }
          }
          style={{ width: "100%", height: 350 }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          attributionControl={false}
          ref={mapRef}
          onLoad={() => setMapReady(true)}
          onError={() => setMapError(true)}
        >
          <NavigationControl position="bottom-right" />
          <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}>
            <button
              type="button"
              onClick={openMapInNewTab}
              style={{
                background: "#ffffff",
                border: "1px solid #cfd8dc",
                borderRadius: 4,
                padding: "6px 10px",
                fontSize: 12,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            >
              Open map
            </button>
          </div>
          {routeGeoJson && (
            <Source id="pickup-drop-source" type="geojson" data={routeGeoJson}>
              <Layer {...lineLayer} />
            </Source>
          )}
          {routeStops.map((stop, index) => {
            const isPickup = stop.serviceType === "P";
            const label = stop.markerLabel || (isPickup ? "P" : "D");
            const isDelivered = stop.trackingCode === "DELIVERYSUCCESS";
            const color = isDelivered ? "#0d47a1" : (isPickup ? "#1e88e5" : "#d32f2f");
            const backgroundColor = isDelivered ? "#0d47a1" : "#ffffff";
            const textColor = isDelivered ? "#ffffff" : color;
            return (
              <Marker
                key={`${stop.longitude}-${stop.latitude}-${index}`}
                longitude={stop.longitude}
                latitude={stop.latitude}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor,
                    border: `2px solid ${color}`,
                    color: textColor,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                    cursor: "pointer",
                  }}
                  title={isPickup ? "Pickup" : "Drop"}
                  onClick={() =>
                    setActivePopup({
                      longitude: stop.longitude,
                      latitude: stop.latitude,
                      postCode: stop.postCode,
                      label,
                    })
                  }
                >
                  {label}
                </div>
              </Marker>
            );
          })}
          {driverPoint && (
            <Marker longitude={driverPoint.longitude} latitude={driverPoint.latitude}>
              <img
                src={driverIconUrl}
                alt="Driver"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setActivePopup({
                    longitude: driverPoint.longitude,
                    latitude: driverPoint.latitude,
                    postCode: undefined,
                    label: "Driver",
                  })
                }
              />
            </Marker>
          )}
          {activePopup && (
            <Popup
              longitude={activePopup.longitude}
              latitude={activePopup.latitude}
              onClose={() => setActivePopup(null)}
              closeOnClick={false}
              anchor="top"
            >
              <div style={{ fontSize: 12 }}>
                {activePopup.label ? (
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{activePopup.label}</div>
                ) : null}
                <div>{activePopup.postCode }</div>
              </div>
            </Popup>
          )}
        </Map>
      )}
    </div>
  );
}
